import React, { useEffect, useMemo, useState } from 'react';
import CrmImportadorHistorico from './CrmImportadorHistorico';
import CrmHistoricoComercial from './CrmHistoricoComercial';
import CrmOportunidadeDetalhe from './CrmOportunidadeDetalhe';
import CrmEmails from './CrmEmails';

const fmt = valor =>
  Number(valor || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });

const dataBR = valor => {
  if (!valor) return '—';
  const data = new Date(valor);
  return Number.isNaN(data.getTime()) ? '—' : data.toLocaleDateString('pt-BR');
};

async function crmFetch(caminho, opcoes = {}) {
  const token = localStorage.getItem('flowtech_token');
  const resposta = await fetch(caminho, {
    ...opcoes,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opcoes.headers || {}),
    },
    body: opcoes.body ? JSON.stringify(opcoes.body) : undefined,
  });

  const dados = await resposta.json().catch(() => null);

  if (!resposta.ok) {
    throw new Error(dados?.error || `Erro HTTP ${resposta.status}`);
  }

  return dados;
}

function Janela({ titulo, children, onClose, onSave, salvando }) {
  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 820, width: '94%' }}>
        <div className="modal-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
          <span className="modal-title">{titulo}</span>
          <button className="modal-close" onClick={onClose} style={{ border: 0, background: 'transparent', fontSize: 24, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        <div className="modal-body">{children}</div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={onSave} disabled={salvando}>
            {salvando ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CrmComercial({ setPage, currentUser }) {
  const [aba, setAba] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [etapas, setEtapas] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [oportunidades, setOportunidades] = useState([]);
  const [atividades, setAtividades] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [modal, setModal] = useState(null);
  const [oportunidadeDetalheId, setOportunidadeDetalheId] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [busca, setBusca] = useState('');

  const [empresaForm, setEmpresaForm] = useState({
    razao_social: '',
    nome_fantasia: '',
    cnpj: '',
    segmento: '',
    telefone: '',
    email: '',
    cidade: '',
    estado: '',
    origem: '',
  });

  const [oportunidadeForm, setOportunidadeForm] = useState({
    titulo: '',
    empresa_id: '',
    etapa_codigo: 'lead',
    valor_estimado: '',
    previsao_fechamento: '',
    prioridade: 'media',
    origem: 'E-mail',
    descricao: '',
  });

  const [atividadeForm, setAtividadeForm] = useState({
    titulo: '',
    oportunidade_id: '',
    empresa_id: '',
    tipo: 'followup',
    agendada_para: '',
    descricao: '',
  });

  const carregar = async () => {
    setCarregando(true);
    setErro('');

    try {
      const [painel, listaEtapas, listaEmpresas, listaOportunidades, listaAtividades] =
        await Promise.all([
          crmFetch('/api/crm/dashboard'),
          crmFetch('/api/crm/etapas'),
          crmFetch('/api/crm/empresas'),
          crmFetch('/api/crm/oportunidades'),
          crmFetch('/api/crm/atividades?status=pendente'),
        ]);

      setDashboard(painel);
      setEtapas(listaEtapas || []);
      setEmpresas(listaEmpresas || []);
      setOportunidades(listaOportunidades || []);
      setAtividades(listaAtividades || []);
    } catch (e) {
      setErro(e.message);
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  const oportunidadesFiltradas = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (!termo) return oportunidades;

    return oportunidades.filter(item =>
      [
        item.titulo,
        item.empresa_razao_social,
        item.empresa_nome_fantasia,
        item.contato_nome,
      ].some(valor => String(valor || '').toLowerCase().includes(termo))
    );
  }, [oportunidades, busca]);

  const abrirEmpresa = () => {
    setEmpresaForm({
      razao_social: '',
      nome_fantasia: '',
      cnpj: '',
      segmento: '',
      telefone: '',
      email: '',
      cidade: '',
      estado: '',
      origem: '',
    });
    setModal('empresa');
  };

  const abrirOportunidade = () => {
    if (!empresas.length) {
      setAba('empresas');
      abrirEmpresa();
      return;
    }

    setOportunidadeForm({
      titulo: '',
      empresa_id: '',
      etapa_codigo: 'lead',
      valor_estimado: '',
      previsao_fechamento: '',
      prioridade: 'media',
      origem: 'E-mail',
      descricao: '',
    });
    setModal('oportunidade');
  };

  const abrirAtividade = () => {
    if (!empresas.length) {
      setAba('empresas');
      abrirEmpresa();
      return;
    }

    setAtividadeForm({
      titulo: '',
      oportunidade_id: '',
      empresa_id: '',
      tipo: 'followup',
      agendada_para: '',
      descricao: '',
    });
    setModal('atividade');
  };

  const salvarEmpresa = async () => {
    if (!empresaForm.razao_social.trim()) {
      setErro('Informe a razão social.');
      return;
    }

    setSalvando(true);
    setErro('');

    try {
      await crmFetch('/api/crm/empresas', {
        method: 'POST',
        body: empresaForm,
      });
      setModal(null);
      await carregar();
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  };

  const salvarOportunidade = async () => {
    if (!oportunidadeForm.titulo.trim() || !oportunidadeForm.empresa_id) {
      setErro('Informe o título e a empresa.');
      return;
    }

    setSalvando(true);
    setErro('');

    try {
      await crmFetch('/api/crm/oportunidades', {
        method: 'POST',
        body: {
          ...oportunidadeForm,
          empresa_id: Number(oportunidadeForm.empresa_id),
          valor_estimado: Number(oportunidadeForm.valor_estimado || 0),
        },
      });
      setModal(null);
      await carregar();
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  };

  const salvarAtividade = async () => {
    if (
      !atividadeForm.titulo.trim() ||
      (!atividadeForm.oportunidade_id && !atividadeForm.empresa_id)
    ) {
      setErro('Informe o título e vincule a atividade.');
      return;
    }

    setSalvando(true);
    setErro('');

    try {
      await crmFetch('/api/crm/atividades', {
        method: 'POST',
        body: {
          ...atividadeForm,
          oportunidade_id: atividadeForm.oportunidade_id
            ? Number(atividadeForm.oportunidade_id)
            : null,
          empresa_id: atividadeForm.empresa_id
            ? Number(atividadeForm.empresa_id)
            : null,
          agendada_para: atividadeForm.agendada_para || null,
        },
      });
      setModal(null);
      await carregar();
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  };

  const moverOportunidade = async (item, etapa_codigo) => {
    setErro('');

    try {
      await crmFetch(`/api/crm/oportunidades/${item.id}`, {
        method: 'PUT',
        body: { etapa_codigo },
      });
      await carregar();
    } catch (e) {
      setErro(e.message);
    }
  };

  const concluirAtividade = async atividade => {
    setErro('');

    try {
      await crmFetch(`/api/crm/atividades/${atividade.id}`, {
        method: 'PUT',
        body: { status: 'concluida' },
      });
      await carregar();
    } catch (e) {
      setErro(e.message);
    }
  };

  const abas = [
    ['dashboard', 'Visão geral'],
    ['funil', 'Funil comercial'],
    ['historico', 'Histórico'],
    ['emails', 'E-mails'],
    ['empresas', 'Empresas'],
    ['atividades', 'Atividades'],
  ];

  if (carregando && !dashboard) {
    return <div className="card" style={{ padding: 30 }}>Carregando CRM...</div>;
  }

  return (
    <>
      <div className="card" style={{ marginBottom: 18 }}>
        <div className="card-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <div className="card-title">Central de relacionamento</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
              Empresas, oportunidades, atividades e acompanhamento comercial.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="btn btn-ghost" onClick={abrirEmpresa}>
              + Empresa
            </button>
            <button className="btn btn-primary" onClick={abrirOportunidade}>
              + Oportunidade
            </button>
            <button className="btn btn-ghost" onClick={abrirAtividade}>
              + Atividade
            </button>
            <CrmImportadorHistorico currentUser={currentUser} onImportado={carregar} />
          </div>
        </div>

        <div style={{
          display: 'flex',
          gap: 8,
          padding: '0 16px 16px',
          flexWrap: 'wrap',
        }}>
          {abas.map(([id, label]) => (
            <button
              key={id}
              className={`btn btn-sm ${aba === id ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setAba(id)}
            >
              {label}
            </button>
          ))}
          <button className="btn btn-sm btn-ghost" onClick={carregar}>
            Atualizar
          </button>
        </div>
      </div>

      {erro && (
        <div
          className="card"
          style={{
            padding: 14,
            marginBottom: 18,
            borderColor: 'var(--danger)',
            color: 'var(--danger)',
          }}
        >
          {erro}
        </div>
      )}

      {aba === 'dashboard' && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 16, marginBottom: 18 }}>
            <div className="kpi-card">
              <div className="kpi-label">Oportunidades abertas</div>
              <div className="kpi-value">{dashboard?.oportunidades_abertas || 0}</div>
              <div className="kpi-sub">em andamento</div>
            </div>
            <div className="kpi-card">
              <div className="kpi-label">Pipeline</div>
              <div className="kpi-value" style={{ fontSize: 23 }}>
                {fmt(dashboard?.valor_pipeline)}
              </div>
              <div className="kpi-sub">valor potencial</div>
            </div>
            <div className="kpi-card">
              <div className="kpi-label">Ganhos no mês</div>
              <div className="kpi-value">{dashboard?.ganhos_no_mes || 0}</div>
              <div className="kpi-sub">{fmt(dashboard?.valor_ganho_no_mes)}</div>
            </div>
            <div className="kpi-card">
              <div className="kpi-label">Atividades pendentes</div>
              <div className="kpi-value">{dashboard?.atividades_pendentes || 0}</div>
              <div className="kpi-sub">próximas ações</div>
            </div>
          </div>

          <div className="card">
            <div className="card-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span className="card-title">Resumo do funil</span>
              <button className="btn btn-sm btn-primary" onClick={() => setAba('funil')}>
                Abrir funil
              </button>
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Etapa</th>
                    <th>Oportunidades</th>
                    <th>Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {(dashboard?.funil || []).map(item => (
                    <tr key={item.codigo}>
                      <td>
                        <span
                          className="status"
                          style={{ background: item.cor, color: '#fff' }}
                        >
                          {item.nome}
                        </span>
                      </td>
                      <td>{item.quantidade}</td>
                      <td>{fmt(item.valor)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {aba === 'funil' && (
        <>
          <div className="card" style={{ marginBottom: 18 }}>
            <div className="filter-row">
              <input
                className="search-input"
                placeholder="Buscar oportunidade ou empresa..."
                value={busca}
                onChange={e => setBusca(e.target.value)}
              />
            </div>
          </div>

          <div style={{ overflowX: 'auto', paddingBottom: 10 }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${etapas.length || 1}, minmax(220px, 1fr))`,
              gap: 10,
              minWidth: etapas.length * 230,
            }}>
              {etapas.map(etapa => {
                const itens = oportunidadesFiltradas.filter(
                  item => item.etapa_codigo === etapa.codigo
                );
                const total = itens.reduce(
                  (soma, item) => soma + Number(item.valor_estimado || 0),
                  0
                );

                return (
                  <div
                    key={etapa.codigo}
                    style={{
                      background: 'var(--surface2)',
                      border: '1px solid var(--border)',
                      borderTop: `4px solid ${etapa.cor}`,
                      borderRadius: 12,
                      padding: 10,
                      minHeight: 300,
                    }}
                  >
                    <div style={{ marginBottom: 10 }}>
                      <strong style={{ fontSize: 13 }}>{etapa.nome}</strong>
                      <div style={{ fontSize: 10, color: 'var(--muted)' }}>
                        {itens.length} oportunidade(s) · {fmt(total)}
                      </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {!itens.length && (
                        <div style={{
                          border: '1px dashed var(--border)',
                          borderRadius: 9,
                          padding: 12,
                          fontSize: 11,
                          color: 'var(--muted)',
                        }}>
                          Nenhuma oportunidade nesta etapa.
                        </div>
                      )}

                      {itens.map(item => (
                        <div
                          key={item.id}
                          onClick={() => setOportunidadeDetalheId(item.id)}
                          style={{
                            background: 'var(--surface)',
                            border: '1px solid var(--border)',
                            borderRadius: 10,
                            padding: 10,
                          }}
                        >
                          <strong style={{ fontSize: 12 }}>{item.titulo}</strong>
                          <div style={{
                            fontSize: 11,
                            color: 'var(--muted)',
                            margin: '5px 0 8px',
                          }}>
                            {item.empresa_nome_fantasia ||
                              item.empresa_razao_social}
                          </div>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            fontSize: 11,
                            marginBottom: 8,
                          }}>
                            <span>{item.prioridade}</span>
                            <strong>{fmt(item.valor_estimado)}</strong>
                          </div>
                          <select
                            value={item.etapa_codigo}
                            onClick={e => e.stopPropagation()}
                            onChange={e => moverOportunidade(item, e.target.value)}
                            style={{ width: '100%', fontSize: 11 }}
                          >
                            {etapas.map(opcao => (
                              <option key={opcao.codigo} value={opcao.codigo}>
                                {opcao.nome}
                              </option>
                            ))}
                          </select>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {aba === 'emails' && (
        <CrmEmails
          crmFetch={crmFetch}
          currentUser={currentUser}
        />
      )}

      {aba === 'historico' && (
        <CrmHistoricoComercial
          oportunidades={oportunidades}
          etapas={etapas}
        />
      )}

      {aba === 'empresas' && (
        <div className="card">
          <div className="card-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span className="card-title">Empresas e clientes</span>
            <button className="btn btn-primary" onClick={abrirEmpresa}>
              Nova empresa
            </button>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Empresa</th>
                  <th>CNPJ</th>
                  <th>Contato</th>
                  <th>Cidade/UF</th>
                  <th>Oportunidades</th>
                  <th>Pipeline</th>
                </tr>
              </thead>
              <tbody>
                {!empresas.length ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', padding: 30 }}>
                      Nenhuma empresa cadastrada.
                    </td>
                  </tr>
                ) : empresas.map(empresa => (
                  <tr key={empresa.id}>
                    <td>
                      <strong>
                        {empresa.nome_fantasia || empresa.razao_social}
                      </strong>
                      <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                        {empresa.nome_fantasia ? empresa.razao_social : empresa.segmento}
                      </div>
                    </td>
                    <td>{empresa.cnpj || '—'}</td>
                    <td>
                      {empresa.telefone || empresa.email || '—'}
                    </td>
                    <td>
                      {[empresa.cidade, empresa.estado].filter(Boolean).join('/') || '—'}
                    </td>
                    <td>{empresa.total_oportunidades}</td>
                    <td>{fmt(empresa.valor_pipeline)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {aba === 'atividades' && (
        <div className="card">
          <div className="card-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span className="card-title">Atividades pendentes</span>
            <button className="btn btn-primary" onClick={abrirAtividade}>
              Nova atividade
            </button>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Atividade</th>
                  <th>Empresa</th>
                  <th>Tipo</th>
                  <th>Agendada</th>
                  <th>Ação</th>
                </tr>
              </thead>
              <tbody>
                {!atividades.length ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', padding: 30 }}>
                      Nenhuma atividade pendente.
                    </td>
                  </tr>
                ) : atividades.map(atividade => (
                  <tr key={atividade.id}>
                    <td>
                      <strong>{atividade.titulo}</strong>
                      <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                        {atividade.oportunidade_titulo || atividade.descricao}
                      </div>
                    </td>
                    <td>{atividade.empresa_razao_social || '—'}</td>
                    <td>{atividade.tipo}</td>
                    <td>{dataBR(atividade.agendada_para)}</td>
                    <td>
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => concluirAtividade(atividade)}
                      >
                        Concluir
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {modal === 'empresa' && (
        <Janela
          titulo="Nova empresa"
          onClose={() => setModal(null)}
          onSave={salvarEmpresa}
          salvando={salvando}
        >
          <div className="form-row form-2">
            <div className="form-group">
              <label>Razão social *</label>
              <input
                value={empresaForm.razao_social}
                onChange={e => setEmpresaForm({
                  ...empresaForm,
                  razao_social: e.target.value,
                })}
              />
            </div>
            <div className="form-group">
              <label>Nome fantasia</label>
              <input
                value={empresaForm.nome_fantasia}
                onChange={e => setEmpresaForm({
                  ...empresaForm,
                  nome_fantasia: e.target.value,
                })}
              />
            </div>
          </div>
          <div className="form-row form-3">
            <div className="form-group">
              <label>CNPJ</label>
              <input
                value={empresaForm.cnpj}
                onChange={e => setEmpresaForm({...empresaForm, cnpj: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label>Segmento</label>
              <input
                value={empresaForm.segmento}
                onChange={e => setEmpresaForm({
                  ...empresaForm,
                  segmento: e.target.value,
                })}
              />
            </div>
            <div className="form-group">
              <label>Origem</label>
              <input
                value={empresaForm.origem}
                onChange={e => setEmpresaForm({
                  ...empresaForm,
                  origem: e.target.value,
                })}
              />
            </div>
          </div>
          <div className="form-row form-2">
            <div className="form-group">
              <label>Telefone</label>
              <input
                value={empresaForm.telefone}
                onChange={e => setEmpresaForm({
                  ...empresaForm,
                  telefone: e.target.value,
                })}
              />
            </div>
            <div className="form-group">
              <label>E-mail</label>
              <input
                type="email"
                value={empresaForm.email}
                onChange={e => setEmpresaForm({
                  ...empresaForm,
                  email: e.target.value,
                })}
              />
            </div>
          </div>
          <div className="form-row form-2">
            <div className="form-group">
              <label>Cidade</label>
              <input
                value={empresaForm.cidade}
                onChange={e => setEmpresaForm({
                  ...empresaForm,
                  cidade: e.target.value,
                })}
              />
            </div>
            <div className="form-group">
              <label>UF</label>
              <input
                maxLength={2}
                value={empresaForm.estado}
                onChange={e => setEmpresaForm({
                  ...empresaForm,
                  estado: e.target.value.toUpperCase(),
                })}
              />
            </div>
          </div>
        </Janela>
      )}

      {modal === 'oportunidade' && (
        <Janela
          titulo="Nova oportunidade"
          onClose={() => setModal(null)}
          onSave={salvarOportunidade}
          salvando={salvando}
        >
          <div className="form-row form-2">
            <div className="form-group">
              <label>Título / oportunidade *</label>
              <input
                value={oportunidadeForm.titulo}
                onChange={e => setOportunidadeForm({
                  ...oportunidadeForm,
                  titulo: e.target.value,
                })}
              />
            </div>
            <div className="form-group">
              <label>Empresa *</label>
              <select
                value={oportunidadeForm.empresa_id}
                onChange={e => setOportunidadeForm({
                  ...oportunidadeForm,
                  empresa_id: e.target.value,
                })}
              >
                <option value="">Selecione</option>
                {empresas.map(empresa => (
                  <option key={empresa.id} value={empresa.id}>
                    {empresa.nome_fantasia || empresa.razao_social}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-row form-4">
            <div className="form-group">
              <label>Etapa</label>
              <select
                value={oportunidadeForm.etapa_codigo}
                onChange={e => setOportunidadeForm({
                  ...oportunidadeForm,
                  etapa_codigo: e.target.value,
                })}
              >
                {etapas.map(etapa => (
                  <option key={etapa.codigo} value={etapa.codigo}>
                    {etapa.nome}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Valor estimado</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={oportunidadeForm.valor_estimado}
                onChange={e => setOportunidadeForm({
                  ...oportunidadeForm,
                  valor_estimado: e.target.value,
                })}
              />
            </div>
            <div className="form-group">
              <label>Previsão</label>
              <input
                type="date"
                value={oportunidadeForm.previsao_fechamento}
                onChange={e => setOportunidadeForm({
                  ...oportunidadeForm,
                  previsao_fechamento: e.target.value,
                })}
              />
            </div>
            <div className="form-group">
              <label>Prioridade</label>
              <select
                value={oportunidadeForm.prioridade}
                onChange={e => setOportunidadeForm({
                  ...oportunidadeForm,
                  prioridade: e.target.value,
                })}
              >
                <option value="baixa">Baixa</option>
                <option value="media">Média</option>
                <option value="alta">Alta</option>
                <option value="urgente">Urgente</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label>Descrição</label>
            <textarea
              rows={4}
              value={oportunidadeForm.descricao}
              onChange={e => setOportunidadeForm({
                ...oportunidadeForm,
                descricao: e.target.value,
              })}
            />
          </div>
        </Janela>
      )}

      {modal === 'atividade' && (
        <Janela
          titulo="Nova atividade"
          onClose={() => setModal(null)}
          onSave={salvarAtividade}
          salvando={salvando}
        >
          <div className="form-row form-2">
            <div className="form-group">
              <label>Título *</label>
              <input
                value={atividadeForm.titulo}
                onChange={e => setAtividadeForm({
                  ...atividadeForm,
                  titulo: e.target.value,
                })}
              />
            </div>
            <div className="form-group">
              <label>Tipo</label>
              <select
                value={atividadeForm.tipo}
                onChange={e => setAtividadeForm({
                  ...atividadeForm,
                  tipo: e.target.value,
                })}
              >
                <option value="followup">Follow-up</option>
                <option value="ligacao">Ligação</option>
                <option value="email">E-mail</option>
                <option value="whatsapp">WhatsApp</option>
                <option value="reuniao">Reunião</option>
                <option value="visita">Visita</option>
                <option value="tarefa">Tarefa</option>
                <option value="nota">Nota</option>
              </select>
            </div>
          </div>
          <div className="form-row form-2">
            <div className="form-group">
              <label>Oportunidade</label>
              <select
                value={atividadeForm.oportunidade_id}
                onChange={e => {
                  const oportunidade = oportunidades.find(
                    item => String(item.id) === e.target.value
                  );
                  setAtividadeForm({
                    ...atividadeForm,
                    oportunidade_id: e.target.value,
                    empresa_id: oportunidade?.empresa_id || atividadeForm.empresa_id,
                  });
                }}
              >
                <option value="">Sem oportunidade</option>
                {oportunidades.map(item => (
                  <option key={item.id} value={item.id}>{item.titulo}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Empresa</label>
              <select
                value={atividadeForm.empresa_id}
                onChange={e => setAtividadeForm({
                  ...atividadeForm,
                  empresa_id: e.target.value,
                })}
              >
                <option value="">Selecione</option>
                {empresas.map(empresa => (
                  <option key={empresa.id} value={empresa.id}>
                    {empresa.nome_fantasia || empresa.razao_social}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label>Agendada para</label>
            <input
              type="datetime-local"
              value={atividadeForm.agendada_para}
              onChange={e => setAtividadeForm({
                ...atividadeForm,
                agendada_para: e.target.value,
              })}
            />
          </div>
          <div className="form-group">
            <label>Descrição</label>
            <textarea
              rows={4}
              value={atividadeForm.descricao}
              onChange={e => setAtividadeForm({
                ...atividadeForm,
                descricao: e.target.value,
              })}
            />
          </div>
        </Janela>
      )}

      {oportunidadeDetalheId && (
        <CrmOportunidadeDetalhe
          oportunidadeId={oportunidadeDetalheId}
          crmFetch={crmFetch}
          onClose={() => setOportunidadeDetalheId(null)}
        />
      )}

      {setPage && aba === 'funil' && (
        <div style={{ marginTop: 18 }}>
          <button className="btn btn-ghost" onClick={() => setPage('propostas')}>
            Abrir propostas comerciais
          </button>
        </div>
      )}
    </>
  );
}
